/** Plays a streamed fragmented MP4 (video/mp4 over chunked transfer) through
 * MediaSource, so playback starts while later frames are still being rendered
 * server-side. Plain `<video src>` cannot do this: browsers stall on a
 * growing MP4 of unknown duration, which is the case MSE exists for. */

// Must match the render service encoder: H.264 Main@3.1 + AAC-LC.
const STREAM_MIME = 'video/mp4; codecs="avc1.4d401f, mp4a.40.2"';

export function canStreamVideo(): boolean {
  return typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported(STREAM_MIME);
}

/** Returns an object URL to hand to a `<video>` element. Fetching and
 * appending starts when the element attaches the MediaSource. Any failure
 * (bad status, mid-stream disconnect, abort) surfaces as a media error on
 * the element, so existing error handling covers every case. */
export function openVideoStream(url: string, signal: AbortSignal): string {
  const mediaSource = new MediaSource();
  mediaSource.addEventListener(
    'sourceopen',
    () => { void feed(mediaSource, url, signal); },
    { once: true },
  );
  return URL.createObjectURL(mediaSource);
}

async function feed(mediaSource: MediaSource, url: string, signal: AbortSignal): Promise<void> {
  const fail = () => {
    try {
      if (mediaSource.readyState === 'open') mediaSource.endOfStream('network');
    } catch {
      // The element already detached; there is nothing left to signal.
    }
  };
  let response: Response;
  try {
    response = await fetch(url, { signal });
  } catch {
    fail();
    return;
  }
  if (!response.ok || !response.body) {
    fail();
    return;
  }
  const reader = response.body.getReader();
  try {
    const buffer = mediaSource.addSourceBuffer(STREAM_MIME);
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      const appended = new Promise<void>((resolve, reject) => {
        buffer.addEventListener('updateend', () => resolve(), { once: true });
        buffer.addEventListener('error', () => reject(new Error('append failed')), { once: true });
      });
      buffer.appendBuffer(value);
      await appended;
    }
    if (mediaSource.readyState === 'open') mediaSource.endOfStream();
  } catch {
    fail();
  } finally {
    reader.releaseLock();
  }
}
